import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import PanelTemplate from "../../templates/Panel";
import BoxDoughnutChart from "../../components/dashboard/BoxDoughnutChart";
import Table from "../../components/Table";
import Box from "../../components/Box";
import UserCell from "../../components/UserCell";
import BoxVerticalBarChart from "../../components/dashboard/BoxVerticalBarChart";
import Icon from "../../components/Icon";
import api from "../../services/api";
import { convertIntToMoney } from "../../helpers/conversions";
import moment from "moment";
import InputDate from "../../components/InputDate";
import Button from "../../components/Button";
import InputCheckboxes from "../../components/InputCheckboxes";
import InputSelect from "../../components/InputSelect";

const createEmptyResourceData = () => ({
    amount_resources: 0,
    average_ticket: 0,
    average_items: 0,
    total_proposals_today: 0,
    average_time_today: 0,
    average_time_month: 0,
    resources_by_status: { statuses: [], totals: { count: 0, amount: 0 } },
    resources_by_admin: [],
    resources_by_month: [],
    items: [],
});

const createEmptyOverview = () => ({
    period: {},
    summary: {},
    funnel: [],
    alerts: [],
    seller_performance: [],
    product_insights: {
        most_budgeted: [],
        most_ordered: [],
        low_conversion: [],
    },
});

const periodPresets = [
    { key: "today", label: "Hoje" },
    { key: "7days", label: "7 dias" },
    { key: "month", label: "Mês atual" },
    { key: "previous-month", label: "Mês anterior" },
    { key: "custom", label: "Personalizado" },
];

const formatMoney = value => `R$ ${convertIntToMoney(value || 0)}`;

const formatNumber = value => Number(value || 0).toLocaleString("pt-BR");

const formatPercent = value => `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: Number(value || 0) % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
})}%`;

const formatMinutes = value => {
    const minutes = Number(value || 0);
    if (minutes < 60) return `${minutes} min`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

const getPeriodRange = preset => {
    const today = moment();

    if (preset === "today") {
        return [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
    }

    if (preset === "7days") {
        return [today.clone().subtract(6, "days").format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
    }

    if (preset === "previous-month") {
        const previousMonth = today.clone().subtract(1, "month");
        return [
            previousMonth.clone().startOf("month").format("YYYY-MM-DD"),
            previousMonth.clone().endOf("month").format("YYYY-MM-DD"),
        ];
    }

    return [
        today.clone().startOf("month").format("YYYY-MM-DD"),
        today.clone().endOf("month").format("YYYY-MM-DD"),
    ];
};

const getStatusParam = (selectedStatuses, statuses) => {
    if (!selectedStatuses || statuses.length === 0) return [];
    if (selectedStatuses.length === statuses.length) return [];
    if (selectedStatuses.length === 0) return ["__none"];
    return selectedStatuses;
};

const buildQuoteListUrl = params => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
            searchParams.set(key, value);
        }
    });

    const search = searchParams.toString();
    return `/quote/list${search ? `?${search}` : ""}`;
};

const StatusDropdown = ({ label, statuses, selectedStatuses, setSelectedStatuses }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const allSelected = statuses.length > 0 && selectedStatuses.length === statuses.length;
    const buttonLabel = allSelected
        ? "Todos os status"
        : selectedStatuses.length === 0
            ? "Nenhum status"
        : `${selectedStatuses.length} status`;

    const options = statuses.map(status => ({
        label: status.name,
        value: status.id,
    }));

    return (
        <div className="status-dropdown" ref={ref}>
            <span className="status-dropdown-label">{label}</span>
            <button
                type="button"
                className="status-dropdown-trigger"
                onClick={() => setOpen(prev => !prev)}
            >
                <span>{buttonLabel}</span>
                <Icon name="arrow-dropdown" size={16} className="chevron" />
            </button>
            {open &&
                <div className="status-dropdown-menu">
                    <div className="status-dropdown-menu-actions">
                        <button type="button" onClick={() => setSelectedStatuses(statuses.map(status => status.id))}>Todos</button>
                        <button type="button" onClick={() => setSelectedStatuses([])}>Nenhum</button>
                    </div>
                    <InputCheckboxes
                        hideBorder={true}
                        options={options}
                        selecteds={selectedStatuses}
                        setSelecteds={setSelectedStatuses}
                    />
                </div>
            }
        </div>
    );
};

const SummaryCard = ({ icon, accent, label, value, loading }) => {
    return (
        <div className={`dashboard-summary-card accent-${accent}`}>
            <div className="dashboard-summary-icon">
                <Icon name={icon} size={20} />
            </div>
            <div className="dashboard-summary-content">
                <span className="dashboard-summary-label">{label}</span>
                <strong className={loading ? "dashboard-summary-value skeleton" : "dashboard-summary-value"}>
                    {loading ? "\u00A0" : value}
                </strong>
            </div>
        </div>
    );
};

const Funnel = ({ steps, loading }) => {
    const maxCount = Math.max(...steps.map(step => step.count || 0), 1);

    return (
        <Box title="Funil comercial" className="dashboard-funnel-box">
            <div className="dashboard-funnel">
                {loading && steps.length === 0 &&
                    <div className="dashboard-empty-state">Carregando...</div>
                }

                {!loading && steps.map((step, index) => {
                    const width = `${Math.max(((step.count || 0) / maxCount) * 100, step.count > 0 ? 8 : 0)}%`;

                    return (
                        <div className="dashboard-funnel-step" key={step.key}>
                            <div className="dashboard-funnel-step-header">
                                <span className="dashboard-funnel-index">{index + 1}</span>
                                <div>
                                    <strong>{step.label}</strong>
                                    <span>
                                        {formatNumber(step.count)} registros
                                        {step.amount !== null ? ` · ${formatMoney(step.amount)}` : ""}
                                        {step.key === "proposals" && step.email_sent_count !== undefined ? ` · ${formatNumber(step.email_sent_count)} com e-mail enviado` : ""}
                                    </span>
                                </div>
                                <b>{formatPercent(step.conversion_rate)}</b>
                            </div>
                            <div className="dashboard-funnel-bar">
                                <span style={{ width }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </Box>
    );
};

const AlertsPanel = ({ alerts, loading }) => (
    <Box title="Atenção agora">
        <div className="dashboard-alerts">
            {loading && alerts.length === 0 &&
                <div className="dashboard-empty-state">Carregando...</div>
            }

            {!loading && alerts.map((alert, index) => (
                <Link to={alert.to} className={`dashboard-alert intent-${alert.intent}`} key={index}>
                    <div className="dashboard-alert-icon">
                        <Icon name={alert.icon} size={18} />
                    </div>
                    <div className="dashboard-alert-content">
                        <span>{alert.title}</span>
                        <strong>{formatNumber(alert.value)}</strong>
                        <p>{alert.description}</p>
                    </div>
                    <Icon name="arrow" size={16} />
                </Link>
            ))}
        </div>
    </Box>
);

const ProductRanking = ({ title, items, emptyText, renderMeta }) => (
    <Box title={title}>
        <div className="dashboard-product-ranking">
            {items.length === 0 &&
                <div className="dashboard-empty-state">{emptyText}</div>
            }

            {items.map((item, index) => (
                <div className="dashboard-product-row" key={`${item.id}-${index}`}>
                    <span className="rank-badge">{index + 1}</span>
                    <div>
                        <strong>{item.sku} - {item.name}</strong>
                        <span>{renderMeta(item)}</span>
                    </div>
                </div>
            ))}
        </div>
    </Box>
);

export const Dashboard = () => {
    const [loading, setLoading] = useState(false);
    const [periodPreset, setPeriodPreset] = useState("month");
    const [startDate, setStartDate] = useState(getPeriodRange("month")[0]);
    const [finalDate, setFinalDate] = useState(getPeriodRange("month")[1]);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [analysisType, setAnalysisType] = useState("budget");

    const [orderStatuses, setOrderStatuses] = useState([]);
    const [budgetStatuses, setBudgetStatuses] = useState([]);
    const [selectedOrderStatuses, setSelectedOrderStatuses] = useState([]);
    const [selectedBudgetStatuses, setSelectedBudgetStatuses] = useState([]);
    const [statusesLoaded, setStatusesLoaded] = useState(false);

    const [admins, setAdmins] = useState([]);
    const [overview, setOverview] = useState(createEmptyOverview());
    const [budgetData, setBudgetData] = useState(createEmptyResourceData());
    const [orderData, setOrderData] = useState(createEmptyResourceData());

    const getData = useCallback(() => {
        setLoading(true);

        api.get(`/dashboard`, {
            params: {
                start_date: startDate,
                final_date: finalDate,
                budget_statuses: getStatusParam(selectedBudgetStatuses, budgetStatuses),
                order_statuses: getStatusParam(selectedOrderStatuses, orderStatuses),
                admin_id: selectedAdmin,
            }
        }).then(res => {
            setAdmins(res.data.admins || []);
            setOverview(res.data.overview || createEmptyOverview());
            setBudgetData(res.data.budget || createEmptyResourceData());
            setOrderData(res.data.order || createEmptyResourceData());
        }).finally(() => {
            setLoading(false);
        });
    }, [budgetStatuses, finalDate, orderStatuses, selectedAdmin, selectedBudgetStatuses, selectedOrderStatuses, startDate]);

    useEffect(() => {
        getStatuses();
    }, []);

    useEffect(() => {
        if (statusesLoaded) {
            getData();
        }
    }, [getData, statusesLoaded]);

    const getStatuses = () => {
        api.get(`/status`).then(res => {
            const orders = res.data.resources.filter(status => status.type === "order");
            const budgets = res.data.resources.filter(status => status.type === "budget");

            setOrderStatuses(orders);
            setBudgetStatuses(budgets);
            setSelectedOrderStatuses(orders.map(status => status.id));
            setSelectedBudgetStatuses(budgets.map(status => status.id));
            setStatusesLoaded(true);
        });
    };

    const handlePeriodPreset = preset => {
        setPeriodPreset(preset);
        if (preset === "custom") return;

        const [start, final] = getPeriodRange(preset);
        setStartDate(start);
        setFinalDate(final);
    };

    const handleCustomStartDate = value => {
        setPeriodPreset("custom");
        setStartDate(value);
    };

    const handleCustomFinalDate = value => {
        setPeriodPreset("custom");
        setFinalDate(value);
    };

    const adminOptions = useMemo(() => ([
        { value: null, label: "Todos vendedores" },
        ...admins.map(admin => ({ value: admin.id, label: admin.name })),
    ]), [admins]);

    const budgetStatusUrlParam = useMemo(() => {
        if (budgetStatuses.length === 0 || selectedBudgetStatuses.length === budgetStatuses.length) return null;
        return selectedBudgetStatuses.length === 0 ? "__none" : selectedBudgetStatuses.join(",");
    }, [budgetStatuses, selectedBudgetStatuses]);

    const sellerRows = useMemo(() => overview.seller_performance.map(seller => ({
        data: [
            <UserCell name={seller.admin.name} photo={seller.admin?.photo_url} />,
            seller.budget_count,
            formatMoney(seller.budget_amount),
            seller.order_count,
            formatMoney(seller.order_amount),
            formatPercent(seller.conversion_rate),
            formatMinutes(seller.average_response_time),
            seller.pending_budgets,
        ],
        to: buildQuoteListUrl({
            admin_id: seller.admin.id,
            created_from: startDate,
            created_to: finalDate,
            statuses: budgetStatusUrlParam,
        }),
    })), [budgetStatusUrlParam, finalDate, overview.seller_performance, startDate]);

    const currentAnalysisData = analysisType === "budget" ? budgetData : orderData;
    const currentResourceLabel = analysisType === "budget" ? "orçamentos" : "pedidos";
    const summary = overview.summary || {};
    const productInsights = overview.product_insights || createEmptyOverview().product_insights;

    return (
        <PanelTemplate id="dashboard">
            <header className="dashboard-header">
                <div className="dashboard-header-title">
                    <h1>Dashboard</h1>
                    <p>Saúde comercial de {moment(startDate).format("DD/MM/YYYY")} a {moment(finalDate).format("DD/MM/YYYY")}.</p>
                </div>
                <Button text="Atualizar" svg="refresh" loading={loading} action={getData} />
            </header>

            <div className="dashboard-toolbar">
                <div className="dashboard-periods">
                    {periodPresets.map(preset => (
                        <button
                            type="button"
                            key={preset.key}
                            className={periodPreset === preset.key ? "active" : ""}
                            onClick={() => handlePeriodPreset(preset.key)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <div className="dashboard-toolbar-grid">
                    <InputDate label="Data inicial" change={handleCustomStartDate} value={startDate} format="YYYY-MM-DD" />
                    <InputDate label="Data final" change={handleCustomFinalDate} value={finalDate} format="YYYY-MM-DD" />
                    <InputSelect
                        label="Vendedor"
                        value={selectedAdmin}
                        options={adminOptions}
                        change={setSelectedAdmin}
                        removeMargin={true}
                    />
                    <StatusDropdown
                        label="Status de orçamento"
                        statuses={budgetStatuses}
                        selectedStatuses={selectedBudgetStatuses}
                        setSelectedStatuses={setSelectedBudgetStatuses}
                    />
                    <StatusDropdown
                        label="Status de pedido"
                        statuses={orderStatuses}
                        selectedStatuses={selectedOrderStatuses}
                        setSelectedStatuses={setSelectedOrderStatuses}
                    />
                </div>
            </div>

            <div className="dashboard-summary-grid">
                <SummaryCard
                    icon="quote"
                    accent="brand"
                    label="Orçamentos"
                    value={formatMoney(summary.budget_amount)}
                    loading={loading}
                />
                <SummaryCard
                    icon="money"
                    accent="coral"
                    label="Pedidos"
                    value={formatMoney(summary.order_amount)}
                    loading={loading}
                />
                <SummaryCard
                    icon="trending"
                    accent="teal"
                    label="Conversão"
                    value={formatPercent(summary.conversion_rate)}
                    loading={loading}
                />
                <SummaryCard
                    icon="ticket"
                    accent="cobalt"
                    label="Ticket médio"
                    value={formatMoney(summary.average_ticket)}
                    loading={loading}
                />
                <SummaryCard
                    icon="clock"
                    accent="brand"
                    label="Tempo de resposta"
                    value={formatMinutes(summary.average_response_time)}
                    loading={loading}
                />
            </div>

            <div className="dashboard-main-grid">
                <Funnel steps={overview.funnel || []} loading={loading} />
                <AlertsPanel alerts={overview.alerts || []} loading={loading} />
            </div>

            <Box className="no-padding dashboard-seller-box" title="Desempenho dos vendedores">
                <Table
                    loading={loading}
                    header={["Vendedor", "Orç.", "Valor orçado", "Ped.", "Faturado", "Conversão", "Resposta", "Pendências"]}
                    rows={sellerRows}
                />
            </Box>

            <div className="dashboard-products-grid">
                <ProductRanking
                    title="Produtos mais orçados"
                    items={productInsights.most_budgeted || []}
                    emptyText="Nenhum produto orçado no período."
                    renderMeta={item => `${formatNumber(item.count)} orçamentos`}
                />
                <ProductRanking
                    title="Produtos mais vendidos"
                    items={productInsights.most_ordered || []}
                    emptyText="Nenhum produto vendido no período."
                    renderMeta={item => `${formatNumber(item.count)} pedidos`}
                />
                <ProductRanking
                    title="Muito orçado, pouco vendido"
                    items={productInsights.low_conversion || []}
                    emptyText="Nenhum desvio relevante no período."
                    renderMeta={item => `${formatNumber(item.budget_count)} orç. · ${formatNumber(item.order_count)} ped. · ${formatPercent(item.conversion_rate)}`}
                />
            </div>

            <section className="dashboard-analysis">
                <div className="dashboard-section-header">
                    <div>
                        <h2>Análise detalhada</h2>
                        <p>Status e evolução mensal de {currentResourceLabel}.</p>
                    </div>
                    <div className="dashboard-tabs">
                        <button
                            className={analysisType === "budget" ? "active" : ""}
                            onClick={() => setAnalysisType("budget")}
                        >
                            <Icon name="quote" size={16} /> Orçamentos
                        </button>
                        <button
                            className={analysisType === "order" ? "active" : ""}
                            onClick={() => setAnalysisType("order")}
                        >
                            <Icon name="order" size={16} /> Pedidos
                        </button>
                    </div>
                </div>

                <div className="row">
                    <div className="col-5">
                        <BoxDoughnutChart
                            type={analysisType}
                            loading={loading}
                            totalByStatus={currentAnalysisData.resources_by_status}
                        />
                    </div>
                    <div className="col-7">
                        <BoxVerticalBarChart
                            totalByMonth={currentAnalysisData.resources_by_month}
                            title={`Evolução de ${currentResourceLabel} por mês`}
                        />
                    </div>
                </div>
            </section>
        </PanelTemplate>
    );
};

export default Dashboard;
